const OUTLINE_LIBRARY_CAP = 10;
/** A generation is free when this workspace+vehicle template has no
 * successful generation yet (its outlineLibrary is empty) - the first
 * outline drawn for a vehicle costs nothing; a redraw or any generation after
 * that costs PRINT_CREDIT_COST.outline as usual. */
export function isFreeOutlineGeneration(template) {
    return !template.outlineLibrary || template.outlineLibrary.length === 0;
}
/** The outline views currently active - what applyOutlineToTemplate applies
 * and what the editor/canvas should draw: a 'ready' outline's own views, or,
 * during a redraw that kept the previous outline visible while it runs, the
 * 'pending' outline's carried-over views. Returns null when there's nothing
 * to show (no outline, a first draw still in progress, failed, or
 * declined/back-compat). */
export function activeOutlineViews(template) {
    const outline = template.outline;
    if (!outline)
        return null;
    if (outline.status === 'ready')
        return outline.views;
    if (outline.status === 'pending' && Object.keys(outline.views).length > 0)
        return outline.views;
    return null;
}
/** Which outline view (if any) backs a given template view: driver_side,
 * front, rear and roof each map to their own generated drawing, and
 * passenger_side reuses the driver_side drawing mirrored horizontally. */
export function outlineViewFor(view) {
    switch (view) {
        case 'driver_side':
            return { source: 'driver_side', mirror: false };
        case 'passenger_side':
            return { source: 'driver_side', mirror: true };
        case 'front':
            return { source: 'front', mirror: false };
        case 'rear':
            return { source: 'rear', mirror: false };
        case 'roof':
            return { source: 'roof', mirror: false };
        default:
            return null;
    }
}
// Each outlined view has one body panel, so the label names the view (the
// spec PDF and panel downloads list panels by label).
const BODY_PANEL_LABEL = {
    driver_side: 'Driver side',
    passenger_side: 'Passenger side',
    front: 'Front',
    rear: 'Rear',
    roof: 'Top',
};
const bodyPanel = (view, box) => ({
    key: 'body',
    label: BODY_PANEL_LABEL[view],
    view,
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
});
/** When the template has active outline views (activeOutlineViews above),
 * applies each generated view independently: driver_side/passenger_side/
 * front/rear swap in the outline's own view dims and replace those panels
 * with a single 'body' panel each (the printable body area measured off that
 * view's drawing) - the passenger_side box is the driver_side box mirrored
 * horizontally - once all three of driver_side/front/rear are present; roof
 * does the same on its own once its view is present. A view whose drawing
 * isn't present yet (or a roof outline saved before it existed) is left
 * parametric. Returns the template unchanged when nothing above applies (no
 * outline, a first draw still generating, failed, or declined). */
export function applyOutlineToTemplate(template) {
    const outlineViews = activeOutlineViews(template);
    if (!outlineViews)
        return template;
    const { driver_side: driverSide, front, rear, roof } = outlineViews;
    const hasSideSet = !!(driverSide && front && rear);
    if (!hasSideSet && !roof)
        return template;
    let views = { ...template.views };
    let panels = [...template.panels];
    if (hasSideSet) {
        const passengerBox = {
            x: driverSide.widthIn - driverSide.bodyBoxIn.x - driverSide.bodyBoxIn.w,
            y: driverSide.bodyBoxIn.y,
            w: driverSide.bodyBoxIn.w,
            h: driverSide.bodyBoxIn.h,
        };
        views = {
            ...views,
            driver_side: { widthIn: driverSide.widthIn, heightIn: driverSide.heightIn },
            passenger_side: { widthIn: driverSide.widthIn, heightIn: driverSide.heightIn },
            front: { widthIn: front.widthIn, heightIn: front.heightIn },
            rear: { widthIn: rear.widthIn, heightIn: rear.heightIn },
        };
        const outlinedSideViews = new Set(['driver_side', 'passenger_side', 'front', 'rear']);
        panels = [
            ...panels.filter((p) => !outlinedSideViews.has(p.view)),
            bodyPanel('driver_side', driverSide.bodyBoxIn),
            bodyPanel('passenger_side', passengerBox),
            bodyPanel('front', front.bodyBoxIn),
            bodyPanel('rear', rear.bodyBoxIn),
        ];
    }
    if (roof) {
        views = { ...views, roof: { widthIn: roof.widthIn, heightIn: roof.heightIn } };
        panels = [...panels.filter((p) => p.view !== 'roof'), bodyPanel('roof', roof.bodyBoxIn)];
    }
    return { ...template, views, panels };
}
// ---------------------------------------------------------------------------
// Outline library - previously generated outlines kept around so a redraw or
// a switch to the default outline never throws away a paid-for drawing.
// ---------------------------------------------------------------------------
/** Prepends a freshly generated outline as the newest library entry, capped
 * at OUTLINE_LIBRARY_CAP (the oldest entry drops off first). */
export function prependOutlineLibraryEntry(library, entry) {
    return [entry, ...(library ?? [])].slice(0, OUTLINE_LIBRARY_CAP);
}
/** Back compat: a template whose outline is 'ready' but was saved before
 * outlineLibrary existed has no library entry for its own active outline -
 * without this, the first redraw or switch to the default outline would
 * silently drop it instead of keeping it selectable. Synthesizes a library
 * entry from the active outline's own requestedAt/views and prepends it,
 * setting activeOutlineId to match. Returns the same template reference
 * (no-op) once the active outline already has a library entry, or when
 * there's no ready outline at all. */
export function normalizeOutlineLibraryTemplate(template) {
    const outline = template.outline;
    if (outline?.status !== 'ready')
        return template;
    const library = template.outlineLibrary ?? [];
    const alreadyPresent = outline.activeOutlineId
        ? library.some((entry) => entry.id === outline.activeOutlineId)
        : library.some((entry) => entry.createdAt === outline.requestedAt);
    if (alreadyPresent)
        return template;
    const legacyEntry = { id: outline.requestedAt, createdAt: outline.requestedAt, views: outline.views };
    return {
        ...template,
        outline: { ...outline, activeOutlineId: legacyEntry.id },
        outlineLibrary: prependOutlineLibraryEntry(library, legacyEntry),
    };
}
//# sourceMappingURL=outline.js.map